import React, { useMemo, useState, useEffect, useCallback } from 'react';

import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import KpiCard from '../../components/KpiCard';
import Header from '../../components/Header';
import { styles, colors, radius, shadows } from '../../styles/theme';
import useCachedQuery from '../../hooks/useCachedQuery';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { searchMatchPre } from '../../utils/searchMatch';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SubeDashboard = () => {
  const { user } = useAuth();

  const [arama, setArama] = useState('');
  const debouncedArama = useDebouncedValue(arama, 300);
  const userCacheKey = user?.id || user?.email || user?.name || 'anon';

  const {
    data: stoklar = [],
    isLoading: stoklarLoading,
    isValidating: stoklarValidating,
    revalidate: revalidateStoklar
  } = useCachedQuery(
    ['dashboard', userCacheKey, 'sube', 'stok'],
    async () => {
      const res = await fetch(`${API_URL}/api/stok`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    { staleTime: 20000 }
  );

  const {
    data: talepler = [],
    isLoading: taleplerLoading,
    isValidating: taleplerValidating,
    revalidate: revalidateTalepler
  } = useCachedQuery(
    ['dashboard', userCacheKey, 'sube', 'talepler'],
    async () => {
      const res = await fetch(`${API_URL}/api/talepler`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    { staleTime: 20000 }
  );

  const {
    data: özet = {},
    isLoading: ozetLoading,
    isValidating: ozetValidating,
    revalidate: revalidateOzet
  } = useCachedQuery(
    ['dashboard', userCacheKey, 'sube', 'özet'],
    async () => {
      const res = await fetch(`${API_URL}/api/raporlar/özet`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return data && typeof data === 'object' ? data : {};
    },
    { staleTime: 20000 }
  );

  const loading = stoklarLoading || taleplerLoading || ozetLoading;
  const isValidating = stoklarValidating || taleplerValidating || ozetValidating;

  // Auto-refresh her 30 saniye
  const refreshAll = useCallback(() => {
    revalidateStoklar({ force: true }).catch(() => {});
    revalidateTalepler({ force: true }).catch(() => {});
    revalidateOzet({ force: true }).catch(() => {});
  }, [revalidateStoklar, revalidateTalepler, revalidateOzet]);

  useEffect(() => {
    const interval = setInterval(refreshAll, 30000);
    return () => clearInterval(interval);
  }, [refreshAll]);

  const stokKayıtları = useMemo(() => {
    const userLokasyonId = user?.lokasyon_id;
    const ilgili = userLokasyonId ? stoklar.filter(s => s.lokasyon_id === userLokasyonId) : stoklar;
    return [...ilgili].sort((a, b) => {
      if (a.durum === 'KRITIK' && b.durum !== 'KRITIK') return -1;
      if (a.durum !== 'KRITIK' && b.durum === 'KRITIK') return 1;
      return (a.malzeme_ad || '').localeCompare(b.malzeme_ad || '');
    });
  }, [stoklar, user]);

  const filteredStokKayıtları = useMemo(() => {
    const q = (debouncedArama || '').trim().toLowerCase();
    if (!q) return stokKayıtları;
    return stokKayıtları.filter(s =>
      searchMatchPre(s.malzeme_ad, q) ||
      searchMatchPre(s.durum, q)
    );
  }, [stokKayıtları, debouncedArama]);

  const bekleyenTalepSayisi = useMemo(() => {
    const bekleyen = talepler.filter(t => t.durum === 'BEKLENIYOR');
    const userLokasyonId = user?.lokasyon_id;
    if (!userLokasyonId) return bekleyen.length;
    return bekleyen.filter(t => t.lokasyon_id === userLokasyonId).length;
  }, [talepler, user]);

  const kritikStokSayisi = stokKayıtları.filter(s => s.durum === 'KRITIK').length;
  const bugunSatis =
    özet?.bugun_satis ??
    özet?.gunluk_satis ??
    özet?.today_sales ??
    null;

  return (
    <div>
      <Header title={`${user?.department || 'Sube'} Panosu`} />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          {hasPermission(user.role, PERMISSIONS.VIEW_FINANCIAL_CIRO) && (
            <KpiCard
              title="Bugun Satış"
              value={loading ? '...' : (bugunSatis != null ? `${Number(bugunSatis).toLocaleString('tr-TR')} TL` : '-')}
              color={styles.success}
              icon="money"
            />
          )}
          <KpiCard title="Bekleyen Talep" value={loading ? '...' : bekleyenTalepSayisi} color={styles.warning} icon="clipboard" />
          <KpiCard title="Kritik Stok" value={loading ? '...' : `${kritikStokSayisi} Urun`} color={styles.danger} icon="warning" />
          <KpiCard title="Aktif Sipariş" value={loading ? '...' : (özet?.aktif_siparis || 0)} color={styles.info} icon="target" />
        </div>

        {/* Hızlı İşlemler — Card Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          marginBottom: '24px'
        }}>
          {[
            { label: 'Sayım Gir', icon: '📊', route: '/stok-depo#stok-sayim', color: '#1565c0' },
            { label: 'Transfer Talep', icon: '🚚', route: '/stok-depo#inventory', color: '#e65100' },
            { label: 'Zayi Bildir', icon: '🗑️', route: '/stok-depo#zayi-girisi', color: '#c62828' },
            { label: 'Stok Durumu', icon: '📦', route: '/stok-depo#stok-durumu', color: '#2e7d32' },
          ].map(item => (
            <div
              key={item.label}
              onClick={() => { window.location.href = item.route; }}
              style={{
                background: colors.surface, borderRadius: radius.xl, padding: '20px 16px',
                textAlign: 'center', cursor: 'pointer', borderLeft: `4px solid ${item.color}`,
                boxShadow: shadows.sm, transition: 'transform 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = shadows.sm; }}
            >
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>{item.icon}</div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: item.color }}>{item.label}</div>
            </div>
          ))}
        </div>

        <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', gap: '12px', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, color: colors.textPrimary, fontSize: '15px', fontWeight: 600, letterSpacing: '-0.01em' }}>Stok Durumu</h3>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                value={arama}
                onChange={(e) => setArama(e.target.value)}
                placeholder="Stok ara"
                style={{ width: '220px', maxWidth: '100%', border: '1px solid #ddd', borderRadius: '6px', padding: '8px 10px', fontSize: '13px' }}
              />
              {isValidating && !loading && (
                <span style={{ fontSize: '12px', color: colors.textSecondary, background: colors.backgroundAlt, borderRadius: '12px', padding: '4px 10px' }}>
                  Güncelleniyor...
                </span>
              )}
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: colors.backgroundAlt }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Urun</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Mevcut</th>
                <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Min. Stok</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Durum</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, row) => (
                  <tr key={`skeleton-${row}`} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    {Array.from({ length: 4 }).map((__, col) => (
                      <td key={`skeleton-${row}-${col}`} style={{ padding: '12px 16px' }}>
                        <div style={{ height: '12px', borderRadius: '4px', background: colors.border, width: col === 0 ? '80%' : '65%' }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filteredStokKayıtları.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding: '20px', textAlign: 'center', color: colors.textSecondary }}>Stok verisi bulunamadı</td>
                </tr>
              ) : (
                filteredStokKayıtları.slice(0, 12).map((stok) => (
                  <tr key={stok.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ padding: '12px 16px' }}>{stok.malzeme_ad || '-'}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>{Number(stok.miktar || 0).toLocaleString('tr-TR')}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>{Number(stok.min_stok || 0).toLocaleString('tr-TR')}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ color: stok.durum === 'KRITIK' ? styles.danger : styles.success }}>
                        {stok.durum === 'KRITIK' ? 'Kritik' : 'Yeterli'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SubeDashboard;
